import { useMemo, useState } from 'react'
import { CheckIcon, ChevronDownIcon, SearchIcon, type LucideIcon } from 'lucide-react'

import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { cn } from '../../lib/utils'

export interface MultiSelectOption {
  value: string
  label: string
  count?: number
}

export interface MultiSelectFilterProps {
  label: string
  icon?: LucideIcon
  options: readonly MultiSelectOption[]
  selected: readonly string[]
  onChange: (next: string[]) => void
  /** When true, show an in-menu search box over option labels. */
  searchable?: boolean
  searchPlaceholder?: string
  emptyLabel?: string
  className?: string
}

/**
 * Checkbox-style multiselect filter matching the Insights/Configure redesign.
 * Empty `selected` means "All" (no filter).
 */
export function MultiSelectFilter({
  label,
  icon: Icon,
  options,
  selected,
  onChange,
  searchable = false,
  searchPlaceholder = 'Search…',
  emptyLabel = 'No matches',
  className
}: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const selectedSet = useMemo(() => new Set(selected), [selected])
  const allSelected = selected.length === 0

  const summary = allSelected
    ? 'All'
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
      : `${selected.length} selected`

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!searchable || q.length === 0) return options
    return options.filter((o) => o.label.toLowerCase().includes(q) || selectedSet.has(o.value))
  }, [options, query, searchable, selectedSet])

  const toggle = (value: string) => {
    if (selectedSet.has(value)) {
      onChange(selected.filter((v) => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setQuery('')
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-8 gap-1.5 px-2.5 text-xs font-medium shadow-sm',
            !allSelected && 'border-brand/40 bg-brand-soft text-brand',
            className
          )}
          aria-expanded={open}
        >
          {Icon && <Icon className="size-3.5 opacity-70" aria-hidden />}
          <span className={allSelected ? 'text-ink-3' : 'text-brand/80'}>{label}:</span>
          <span className={allSelected ? 'text-ink' : 'text-brand'}>{summary}</span>
          <ChevronDownIcon className="size-3 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-62 p-1">
        <div className="flex items-center justify-between px-2 pt-1 pb-1.5">
          <span className="text-10 font-semibold tracking-wider text-ink-3 uppercase">{label}</span>
          <button
            type="button"
            onClick={() => onChange([])}
            className="cursor-pointer border-0 bg-transparent p-0 text-11 text-brand"
          >
            Clear
          </button>
        </div>
        {searchable && (
          <div className="px-1 pb-1.5">
            <div className="flex h-7 items-center gap-1.5 rounded-md border border-line bg-bg px-2">
              <SearchIcon className="size-3 shrink-0 text-ink-4" aria-hidden />
              <Input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-auto min-w-0 flex-1 border-0 bg-transparent p-0 text-xs shadow-none focus-visible:ring-0"
              />
            </div>
          </div>
        )}
        <div className="max-h-66 overflow-y-auto">
          {visible.length === 0 ? (
            <div className="px-2 py-2 text-xs text-ink-4">
              {query.trim() ? `${emptyLabel} “${query.trim()}”` : emptyLabel}
            </div>
          ) : (
            visible.map((opt) => {
              const active = selectedSet.has(opt.value)
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggle(opt.value)}
                  aria-pressed={active}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2 rounded-[0.3125rem] px-2 py-1.5 text-left',
                    active ? 'bg-bg-sunk' : 'bg-transparent hover:bg-bg-sunk/60'
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'inline-flex size-3.5 shrink-0 items-center justify-center rounded-[0.1875rem] border',
                      active ? 'border-brand bg-brand text-bg' : 'border-line bg-bg'
                    )}
                  >
                    {active && <CheckIcon className="size-2.5" strokeWidth={3} />}
                  </span>
                  <span
                    className={cn(
                      'min-w-0 flex-1 truncate text-13',
                      active ? 'text-ink' : 'text-ink-2'
                    )}
                  >
                    {opt.label}
                  </span>
                  {opt.count !== undefined && (
                    <span className="tabular-nums text-11 text-ink-4">{opt.count}</span>
                  )}
                </button>
              )
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
